<template>
  <CreateModal type="addCustomContract" ref="addCustomContract" :customCallButton="true">
    <v-row class="mw-1200 mx-auto" justify="center">
      <v-col cols="12" sm="5" v-if="!addServices">
        <v-form class="mx-auto" ref="addCustomContractForm" v-model="valid">
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="realEstateId"
                :label="$t('contract.label.realEstate')"
                :no-data-text="$t('appointment.filters.noData')"
                :items="realEstateList"
                item-text="name"
                :disabled="disableRealEstate"
                item-value="id"
                :rules="[rules.required]"
                hide-no-data
                clearable
                outlined
              />
            </v-col>
          </v-row>

          <div class="d-block">
            <h3 class="mb-3">{{ $t('contract.label.contractType') }}</h3>
            <v-row>
              <v-col>
                <v-select
                    v-model="paymentType"
                    :no-data-text="$t('appointment.filters.noData')"
                    :items="paymentTypes"
                    :item-text="({ value }) => $t(`paymentTypes.${value}`)"
                    item-value="value"
                    :rules="[rules.required]"
                    hide-details="auto"
                    class="mb-5"
                    single-line
                    outlined
                />
              </v-col>
            </v-row>
          </div>

          <div class="d-block">
            <h3 class="mb-3">{{ $t('contract.label.validity') }}</h3>
            <v-row>
              <v-col cols="6">
                <SelectDate
                  v-model="startDate"
                  :data="startDate"
                  :rules="[rules.required]"
                  :label="$t('contract.label.startDate')"
                />
              </v-col>
              <v-col v-if="needEndDate" cols="6">
                <SelectDate
                  v-model="endDate"
                  :data="endDate"
                  :label="$t('contract.label.endDate')"
                />
              </v-col>
            </v-row>
          </div>

          <v-row>
            <v-col>
              <HTextInput
                :label="$t('contract.label.monthlyCapacity')"
                :dense="false"
                v-model="monthlyCapacity"
                type="number"
                :rules="[rules.positive, rules.maxCapacity]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <HTextInput
                :label="$t('contract.label.monthlyAmount')"
                :dense="false"
                v-model="dailyCapacity"
                type="number"
                :rules="[rules.positive, rules.maxCapacity]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <HTextInput
                :label="$t('contract.label.overuseFee')"
                :placeholder="$t('contract.label.overuseFeePlaceholder')"
                :dense="false"
                v-model="overuseFee"
                type="number"
                :rules="[rules.optionalNotNegative]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn
                @click="goToAddServices"
                :disabled="nextDisabled"
                elevation="0"
                color="primary"
                large
              >
                {{ $t('contract.actions.next') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
      <v-col cols="12" sm="7" v-if="addServices">
        <TableServiceContractValues
          :service-contracts="serviceContracts"
          @deleteServiceContract="deleteServiceContract"
          @editServiceContract="editServiceContract"
        />

        <div class="d-flex justify-end mb-3">
          <v-btn
            @click="openAddServiceModal"
            :loading="isLoading"
            color="primary"
            class="ml-5"
            elevation="0"
            text
          >
            <v-icon left> mdi-plus </v-icon>
            <span class="body-2">
              {{ $t('contract.actions.addServiceContract') }}
            </span>
          </v-btn>
        </div>

        <v-divider />

        <div class="text-right mt-8">
          <v-btn
            @click="goToRealEstateSelect"
            elevation="0"
            depressed
            x-large
          >
            {{ $t('contract.actions.back') }}
          </v-btn>
          <v-btn
            @click="submit"
            :disabled="serviceContracts.length <= 0"
            :loading="isLoading"
            color="primary"
            class="ml-5"
            elevation="0"
            x-large
          >
            {{ $t('contract.actions.createContract') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <AddServicesValues @newServiceCreated="addServiceContract" :serviceTypes="filteredServiceTypes"/>
    <EditServicesValues @updateServiceContract="updateServiceContract" ref="editServiceContractModal" :serviceTypes="serviceTypes"/>
  </CreateModal>
</template>

<script>
import CreateModal from '@/components/app/CreateModal'
import { paymentTypes } from '@/config/constants'
import TableServiceContractValues from '@/components/contract/tableServiceContract/TableServiceContractValues'
import AddServicesValues from '@/components/contract/modalCreateServiceContract/AddServicesValues'
import EditServicesValues from '@/components/contract/modalCreateServiceContract/EditServicesValues'
import { required, maxCapacity, optionalNotNegative } from '@/util/errorHandling'
import { clearCurrency } from '@/util/helpers'
import { toOveruseFee } from '@/util/overuseFee'

import { mapGetters, mapMutations, mapActions } from 'vuex'
import SelectDate from '../../date/SelectDate.vue'
import HTextInput from '../../app/HTextInput.vue'

import moment from 'moment'

export default {
  name: 'modalAddCustomContract',

  components: {
    CreateModal,
    TableServiceContractValues,
    AddServicesValues,
    EditServicesValues,
    SelectDate,
    HTextInput
},

  data() {
    return {
        paymentTypes: paymentTypes,
        paymentType: null,
        addServices: false,
        realEstateId:  null,
        serviceContracts: [],
        filteredServiceTypes: [],
        disableRealEstate: false,
        monthlyCapacity: 0,
        dailyCapacity: 0,
        overuseFee: null,
        startDate: null,
        endDate: null,
        rules: {
          required,
          maxCapacity,
          optionalNotNegative,
          positive: (value) => {
            if (value != '') {
              return (
                parseInt(value) >= 0 ||
                this.$t('rules.minAmount', { value: 0 })
              )
            }

            return true
          },
        },
        valid: false
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'realEstateList', 'serviceTypes']),

    needEndDate() {
      return ['TRIAL', 'SUBSCRIPTION_TRIMESTER', 'SUBSCRIPTION_SEMESTER', 'SUBSCRIPTION_NONAMESTER', 'SUBSCRIPTION_YEAR', 'POSTPAID', 'POSTPAID_SAAS'].includes(this.paymentType)
    },

    nextDisabled() {
      return this.paymentType === null || this.monthlyCapacity === 0 || this.dailyCapacity === 0 || this.realEstateId === null || this.startDate === null
        || (toOveruseFee(this.overuseFee) ?? 0) < 0
    },
  },

  watch: {
    paymentType(val) {
      this.mountFilteredServiceTypes()
      if (this.startDate && val) {
        this.updateEndDate(val, this.startDate)
      }
    },

    startDate(val) {
      if (this.paymentType && val) {
        this.updateEndDate(this.paymentType, val)
      }
    }
  },

  methods: {
    ...mapActions(['realEstateSearch', 'openModal', 'fetchServiceTypes', 'createContract', 'showErrorMessage', 'showSuccessMessage', 'fetchContractsByRealEstate']),
    ...mapMutations(['setIsLoading']),

    openCreateModal(realEstate, paymentType, serviceContracts, startDate, endDate, monthlyCapacity, dailyCapacity, overuseFee) {
      if (realEstate) {
          this.realEstateId = realEstate
          this.disableRealEstate = true
      }

      if (paymentType) {
        this.paymentType = paymentType
      }

      if (serviceContracts) {
        this.serviceContracts = serviceContracts
      }

      if (startDate) {
        this.startDate = startDate
      }

      if (endDate) {
        this.endDate = endDate
      }

      if (monthlyCapacity) {
        this.monthlyCapacity = monthlyCapacity
      }

      if (dailyCapacity) {
        this.dailyCapacity = dailyCapacity
      }

      if (overuseFee !== undefined && overuseFee !== null) {
        this.overuseFee = overuseFee
      }

      this.realEstateSearch()
      this.$refs.addCustomContract.dialog = true
    },

    openAddServiceModal() {
      this.mountFilteredServiceTypes()
      this.openModal('ADD_ITEM_SERVICE_VALUES')
    },

    addServiceContract(value) {
      this.serviceContracts.push(value)
    },

    submit() {
      this.createContract(this.buildContractBody())
        .then(() => {
            this.showSuccessMessage(this.$t('contract.creationSuccessMessage'))
            this.$router.push(`/real-estate-contracts/${this.realEstateId}`)
          })
          .catch(({ data }) => {
            data?.errors && data?.errors?.length >= 1
              ? this.showErrorMessage(data.errors[0].message)
              : this.showErrorMessage(data.message)
          })
          .finally(() => { this.setIsLoading(false) })
    },

    sanitizeServiceContracts(contracts) {
      const sanitizeContracts = contracts.map((contract) => {
        const price = this.getSanitizedValue(contract.price)
        const squareMeterPrice = this.getSanitizedValue(contract.square_meter_price)

        return {
          type: contract.type,
          price: price,
          discount: contract.discount,
          square_meter_price: squareMeterPrice,
          base_square_meters_size: contract.base_square_meters_size,
          host_tax: contract.host_tax,
          displacement_tax: contract.displacement_tax,
          square_meter_tax: contract.square_meter_tax,
          furniture_tax: contract.furniture_tax,
          semi_furniture_tax: contract.semi_furniture_tax,
          house_tax: contract.house_tax,
          trousseau_tax: contract.trousseau_tax,
          host_realty_feature_tax: contract.host_realty_feature_tax,
          urgency_tax: contract.urgency_tax ?? 0,
          host_urgency_tax: contract.host_urgency_tax ?? 0,
          urgency_hours_limit: contract.urgency_hours_limit,
        }
      })

      return sanitizeContracts
    },

    getSanitizedValue(rawValue) {
      return /R\$/.test(rawValue) || /A\$/.test(rawValue)
        ? clearCurrency(rawValue) : rawValue
    },

    buildContractBody() {
      const endDate = this.endDate ? moment(this.endDate).format('YYYY-MM-DD') + 'T12:00:00-03:00' : null
      const contract = {
        subscription_type: this.paymentType,
        real_estate_id: this.realEstateId,
        service_contracts: this.sanitizeServiceContracts(this.serviceContracts),
        start_date: moment(this.startDate).format('YYYY-MM-DD') + 'T12:00:00-03:00',
        end_date: endDate,
        monthly_capacity: this.monthlyCapacity,
        daily_capacity: this.dailyCapacity,
        overuse_fee: toOveruseFee(this.overuseFee)
      }

      return contract
    },

    goToAddServices() {
      this.addServices = true
    },

    goToRealEstateSelect() {
      this.addServices = false
    },

    deleteServiceContract(serviceContract) {
      const propertyValue = serviceContract.type
      const objectIndex = this.serviceContracts.findIndex((item) => item.type === propertyValue)

      if (objectIndex !== -1) this.serviceContracts.splice(objectIndex, 1)
    },

    updateServiceContract(serviceContract) {
      this.deleteServiceContract(serviceContract)
      this.$nextTick(() => this.addServiceContract(serviceContract))
    },

    editServiceContract(serviceContract) {
      this.$refs.editServiceContractModal.openEditModal(serviceContract)
    },

    mountFilteredServiceTypes() {
      Promise.resolve(this.fetchServiceTypes())
        .then(() => {
          let selectedServiceContractTypes = this.serviceContracts.map((item) => item.type)
          this.filteredServiceTypes = this.serviceTypes.filter((item) => !selectedServiceContractTypes.includes(item.value))
        })
    },

    updateEndDate(paymentType, startDate) {
      switch (paymentType) {
        case 'TRIAL':
          this.endDate = moment(startDate).add(1, 'months').format();
          break;
        case 'SUBSCRIPTION_TRIMESTER':
          this.endDate = moment(startDate).add(3, 'months').format();
          break;
        case 'SUBSCRIPTION_SEMESTER':
          this.endDate = moment(startDate).add(6, 'months').format();
          break;
        case 'SUBSCRIPTION_NONAMESTER':
          this.endDate = moment(startDate).add(9, 'months').format();
            break;
        case 'SUBSCRIPTION_YEAR':
          this.endDate = moment(startDate).add(1, 'years').format();
          break;
        default:
          this.endDate = null;
          break;
      }
    }
  },
}
</script>
